import { url } from "../../static/env";
import { fetchJsonWithRetry, loginShouldRetry } from "../../utils/fetchWithRetry";

export async function loginUser(phone, password, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/auth/login',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, password }),
    },
    {
      onRetryProgress: options.onRetryProgress,
      shouldRetryResponse: loginShouldRetry,
    }
  );
  console.log('[login]', { id: data?.id, success: response?.ok });

  // 404/HTML и прочие не-JSON ответы раньше проходили как «успех»
  // (!data.error у строки === true) и крутили login ↔ /client.
  if (!response?.ok) {
    const msg =
      (data && typeof data === 'object' && data.error) ||
      `Ошибка сервера (${response?.status || '?'})`;
    return { error: String(msg) };
  }
  if (!data || typeof data !== 'object') {
    return { error: 'Неверный ответ сервера' };
  }
  if (data.error || data.id == null) {
    return { error: data.error || 'Неверные данные' };
  }
  if (data.support_token) localStorage.setItem('support_token', data.support_token);
  else localStorage.removeItem('support_token');
  return data;
}

export async function getUserData(userId, options = {}) {
  const { data } = await fetchJsonWithRetry(
    url + '/user',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  console.log('getUserData', data);
  return data;
}

export async function getServices() {
  const { data, response } = await fetchJsonWithRetry(url + '/get_services', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  console.log('Received services data:', data);
  return data;
}

export async function sendPhoneCode(phone, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/auth/register/send-code',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Ошибка при отправке кода' };
  }
  return data;
}

export async function registerAccount(phone, code, name, password, accountType = 'business', options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/auth/register',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone,
        code,
        name,
        password,
        account_type: accountType || 'business',
      }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Ошибка регистрации' };
  }
  if (data.support_token) localStorage.setItem('support_token', data.support_token);
  return data;
}

/** @deprecated use registerAccount */
export async function verifyPhoneCode(phone, code, name, password, options = {}) {
  return registerAccount(phone, code, name, password, 'business', options);
}

export async function sendPasswordResetCode(phone, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/auth/password-reset/send-code',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Ошибка при отправке кода' };
  }
  return data;
}

export async function confirmPasswordReset(phone, code, password, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/auth/password-reset/confirm',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, code, password }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Ошибка сброса пароля' };
  }
  return data;
}

export async function getBusinessEmployees(businessUserId, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    `${url}/business/employees?business_user_id=${encodeURIComponent(businessUserId)}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Ошибка загрузки сотрудников', employees: [] };
  }
  return data;
}

export async function inviteBusinessEmployee(businessUserId, { phone } = {}, options = {}) {
  const body = { business_user_id: businessUserId };
  if (phone) body.phone = phone;

  const { data, response } = await fetchJsonWithRetry(
    url + '/business/employees/invite',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось добавить сотрудника' };
  }
  return data;
}

export async function removeBusinessEmployee(businessUserId, employeeUserId, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/business/employees',
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        business_user_id: Number(businessUserId),
        employee_user_id: Number(employeeUserId),
      }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось удалить пользователя' };
  }
  return data;
}

export async function lookupEmployeeByPhone(businessUserId, phone, options = {}) {
  const params = new URLSearchParams({
    business_user_id: String(businessUserId),
    phone: String(phone),
  });
  const { data, response } = await fetchJsonWithRetry(
    `${url}/business/employees/lookup?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    { onRetryProgress: options.onRetryProgress, maxAttempts: 2 }
  );
  if (!response?.ok) {
    return { success: false, error: data?.error || 'Не удалось найти пользователя' };
  }
  return data;
}

export async function assignEmployeeCompany(
  businessUserId,
  employeeUserId,
  { legacyUserId, companyId } = {},
  options = {}
) {
  const body = {
    business_user_id: businessUserId,
    employee_user_id: employeeUserId,
  };
  if (legacyUserId) body.legacy_user_id = legacyUserId;
  if (companyId) body.company_id = companyId;

  const { data, response } = await fetchJsonWithRetry(
    url + '/business/employees/assign-company',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось назначить компанию' };
  }
  return data;
}

export async function getEmployeeCompanyAccess(
  businessUserId,
  employeeUserId,
  { legacyUserId, companyId } = {},
  options = {}
) {
  const params = new URLSearchParams({
    business_user_id: String(businessUserId),
    employee_user_id: String(employeeUserId),
  });
  if (legacyUserId) params.set('legacy_user_id', String(legacyUserId));
  if (companyId) params.set('company_id', String(companyId));

  const { data, response } = await fetchJsonWithRetry(
    `${url}/business/employees/company-access?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось загрузить доступы' };
  }
  return data;
}

export async function saveEmployeeCompanyAccess(payload, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/business/employees/company-access',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось сохранить доступы' };
  }
  return data;
}

export async function deleteBusinessCompany(businessUserId, companyId, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    `${url}/business/companies/${encodeURIComponent(companyId)}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        business_user_id: Number(businessUserId),
      }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось удалить компанию' };
  }
  return data;
}

/** Архив всех компаний аккаунта (удаление профиля). */
export async function archiveAccountCompanies(businessUserId, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    `${url}/business/archive-account-companies`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        business_user_id: Number(businessUserId),
      }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось архивировать компании' };
  }
  return data;
}

export async function removeEmployeeCompanyAccess(employeeUserId, companyId, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/employee/company-access',
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        employee_user_id: Number(employeeUserId),
        company_id: Number(companyId),
      }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось удалить компанию из аккаунта' };
  }
  return data;
}

export async function getEmployeeAccessibleCompanies(employeeUserId, section, options = {}) {
  const params = new URLSearchParams({
    employee_user_id: String(employeeUserId),
    section: String(section || 'all'),
  });
  if (options.inWork === true || options.inWork === 1) {
    params.set('in_work', '1');
  } else if (options.inWork === false || options.inWork === 0) {
    params.set('in_work', '0');
  }
  const { data, response } = await fetchJsonWithRetry(
    `${url}/employee/accessible-companies?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось загрузить компании', companies: [] };
  }
  return data;
}

export async function getEmployeeAccessibleChats(
  employeeUserId,
  { companyId, legacyUserId } = {},
  options = {}
) {
  const params = new URLSearchParams({
    employee_user_id: String(employeeUserId),
  });
  if (companyId) params.set('company_id', String(companyId));
  if (legacyUserId) params.set('legacy_user_id', String(legacyUserId));

  const { data, response } = await fetchJsonWithRetry(
    `${url}/employee/accessible-chats?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось загрузить чаты', chats: [] };
  }
  return data;
}

export async function getBusinessCompanyChats(
  businessUserId,
  companyId,
  options = {}
) {
  const params = new URLSearchParams({
    business_user_id: String(businessUserId),
    company_id: String(companyId),
  });
  const { data, response } = await fetchJsonWithRetry(
    `${url}/business/company-chats?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось загрузить чаты', chats: [] };
  }
  return data;
}

export async function createBusinessCompanyChat(
  businessUserId,
  companyId,
  title,
  options = {}
) {
  const { data, response } = await fetchJsonWithRetry(
    `${url}/business/company-chats`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        business_user_id: Number(businessUserId),
        company_id: Number(companyId),
        title: String(title || '').trim(),
      }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось создать чат' };
  }
  return data;
}

export async function suggestPartyByQuery(query, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/dadata/party',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { suggestions: [], error: data?.error || 'Ошибка DaData' };
  }
  return data;
}

export async function findPartyByInn(query, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/dadata/party-find',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { suggestions: [], error: data?.error || 'Ошибка DaData' };
  }
  return data;
}

export async function getBusinessCompanies(businessUserId, options = {}) {
  const params = new URLSearchParams({
    business_user_id: String(businessUserId),
  });
  if (options.inWork === true || options.inWork === 1) {
    params.set('in_work', '1');
  } else if (options.inWork === false || options.inWork === 0) {
    params.set('in_work', '0');
  }
  const { data, response } = await fetchJsonWithRetry(
    `${url}/business/companies?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось загрузить компании', companies: [] };
  }
  return data;
}

export async function setBusinessCompanyInWork(businessUserId, companyId, inWork, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    `${url}/business/companies/${encodeURIComponent(companyId)}/in-work`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        business_user_id: Number(businessUserId),
        in_work: !!inWork,
      }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось обновить статус компании' };
  }
  return data;
}

export async function setEmployeeCompanyInWork(employeeUserId, companyId, inWork, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    `${url}/employee/company-access/in-work`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        employee_user_id: Number(employeeUserId),
        company_id: Number(companyId),
        in_work: !!inWork,
      }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось обновить статус компании' };
  }
  return data;
}

export async function createBusinessCompany(payload, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/business/companies',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось добавить компанию' };
  }
  return data;
}

export async function updateUserProfile(userId, name, options = {}) {
  const { data, response } = await fetchJsonWithRetry(
    url + '/auth/profile/update',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, name }),
    },
    { onRetryProgress: options.onRetryProgress }
  );
  if (!response.ok) {
    return { success: false, error: data?.error || 'Не удалось сохранить профиль' };
  }
  return data;
}

/** Сжимает фото до аватара (~512px JPEG), чтобы не ловить nginx 413 на base64. */
async function compressImageToJpegDataUrl(file, { maxSide = 512, quality = 0.82 } = {}) {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas unsupported');
    ctx.drawImage(bitmap, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    return dataUrl;
  } finally {
    if (typeof bitmap.close === 'function') bitmap.close();
  }
}

export async function uploadUserPhoto(userId, file, options = {}) {
  let dataUrl;
  try {
    dataUrl = await compressImageToJpegDataUrl(file);
  } catch (e) {
    console.warn('[uploadUserPhoto] compress failed, fallback to original', e);
    dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Не удалось прочитать файл'));
      reader.readAsDataURL(file);
    });
  }

  const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/i);
  if (!match) {
    return { success: false, error: 'Некорректный файл изображения' };
  }
  const photoMime = match[1];
  const photoBase64 = match[2];
  // После сжатия обычно < 200KB; если всё ещё огромно — не шлём
  if (photoBase64.length > 900 * 1024) {
    return {
      success: false,
      error: 'Фото слишком большое даже после сжатия. Выберите другое изображение.',
    };
  }

  try {
    const { data, response } = await fetchJsonWithRetry(
      url + '/auth/profile/photo',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          photo_base64: photoBase64,
          photo_mime: photoMime,
        }),
      },
      {
        onRetryProgress: options.onRetryProgress,
        maxAttempts: 2,
        shouldRetryResponse: (response, _data, err) => {
          if (err) return true;
          if (!response) return true;
          // 413 не ретраим — тело слишком большое на прокси
          if (response.status === 413) return false;
          return response.status >= 500 || response.status === 408 || response.status === 429;
        },
      }
    );
    if (!response || !response.ok) {
      const status = response?.status;
      if (status === 413) {
        return { success: false, error: 'Сервер отклонил файл (слишком большой). Попробуйте другое фото.' };
      }
      return { success: false, error: data?.error || 'Не удалось загрузить фото' };
    }
    return { success: true, ...data };
  } catch (e) {
    return {
      success: false,
      error: e?.userMessage || e?.message || 'Не удалось загрузить фото',
    };
  }
}
